import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Card, Form, Badge } from 'react-bootstrap';
import { v4 } from 'uuid';
import moment from 'moment';
import { FaFilter, FaArrowUp, FaArrowDown } from 'react-icons/fa';

import Stats1 from '../../components/Dashboards/Stats1';
import axios from '../../services/axios';
import Loading from '../../components/Loading';
import ErrorProcessor from '../../components/ErrorProcessor';
import Activity from '../../components/Activity';
import BarChart from '../../components/Charts/BarChart';
import ButtonModal from '../../components/ModalConfirmation/ButtonModal';
import DatepickerComponent from '../../components/Datepicker';
import DonutChart from '../../components/Charts/DonutChart';

export default function DashLeads() {
  const [isLoading, setIsLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [leads, setLeads] = useState([]);
  const startDate = useRef(moment().format('YYYY-MM-DD'));
  const endDate = useRef(moment().format('YYYY-MM-DD'));

  const getData = useCallback(async (loading = true) => {
    if (loading) {
      setIsLoading(true);
    }
    try {
      const getLeads = await axios.get(
        `/leads/dashboards/${startDate.current}/${endDate.current}`
      );
      setLeads(getLeads.data);
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
      setDataLoaded(true);
    }
  }, []);

  const handleDateChange = useCallback((date) => {
    const dateChange = date.start || date.end;

    if (!dateChange) {
      return;
    }

    const srcDate = new Date(dateChange);
    const formatedDate = moment(srcDate).format('YYYY-MM-DD');

    if (date.start) {
      startDate.current = formatedDate;
    }

    if (date.end) {
      endDate.current = formatedDate;
    }
  }, []);

  useEffect(() => {
    getData();
    const intervalId = setInterval(() => {
      getData(false);
    }, 60000);

    return () => clearInterval(intervalId);
  }, [getData]);

  return (
    <div>
      <ErrorProcessor error={apiErrors} />
      <Loading isLoading={isLoading} />
      {dataLoaded && !apiErrors && (
        <>
          <Row className="mb-2">
            <Col sm={4} lg={4} key={v4()} className="mb-4">
              <Stats1
                color={leads.percentageChangeToday >= 0 ? 'green' : 'danger'}
                icon={
                  leads.percentageChangeToday >= 0
                    ? 'sales-performance-up-1'
                    : 'sales-up-1'
                }
                number={leads.todayLeads.toLocaleString('pt-BR')}
                name="Leads de hoje"
                footer={`${leads.percentageChangeToday > 0 ? '+' : ''}
          ${leads.percentageChangeToday.toLocaleString('pt-BR')}% / Ontem`}
              />
            </Col>
            <Col sm={4} lg={4} key={v4()} className="mb-4">
              <Stats1
                color={leads.percentageChangeWeek >= 0 ? 'green' : 'danger'}
                icon={
                  leads.percentageChangeWeek >= 0
                    ? 'sales-performance-up-1'
                    : 'sales-up-1'
                }
                number={leads.weekLeads.toLocaleString('pt-BR')}
                name="Leads da semana"
                footer={`${leads.percentageChangeWeek > 0 ? '+' : ''}
          ${leads.percentageChangeWeek.toLocaleString('pt-BR')}% / Semana passada`}
              />
            </Col>
            <Col sm={4} lg={4} key={v4()} className="mb-4">
              <Stats1
                color={leads.percentageChangeMonth >= 0 ? 'green' : 'danger'}
                icon={
                  leads.percentageChangeMonth >= 0
                    ? 'sales-performance-up-1'
                    : 'sales-up-1'
                }
                number={leads.monthLeads.toLocaleString('pt-BR')}
                name="Leads do mês"
                footer={`${leads.percentageChangeMonth > 0 ? '+' : ''}
          ${leads.percentageChangeMonth.toLocaleString('pt-BR')}% / Mês passado`}
              />
            </Col>
          </Row>
          <Row className="mb-1">
            <Col lg={1}>
              <ButtonModal
                onConfirm={() => getData()}
                message={
                  <div className="mb-3">
                    <Form.Label>Informe as datas:</Form.Label>
                    <DatepickerComponent
                      range
                      maxNumberOfDates={62}
                      defaultValue={{
                        start: moment(startDate.current).format('DD/MM/YYYY'),
                        end: moment(endDate.current).format('DD/MM/YYYY'),
                      }}
                      addProps={{
                        onChange: handleDateChange,
                      }}
                    />
                  </div>
                }
                buttonProps={{
                  variant: 'secondary',
                  className: 'shadow text-white',
                  size: 'sm',
                  title: 'Abrir filtro',
                }}
                textModal={{
                  title: 'Filtrar pesquisa',
                  textConfirm: 'Filtrar',
                }}
              >
                <FaFilter />
              </ButtonModal>
            </Col>
            <Col lg={6}>
              <h6 className="page-heading">
                <i className="text-small">Filtro atual </i>
                <b>{`${moment(startDate.current).format('DD/MM/YYYY')} - ${moment(endDate.current).format('DD/MM/YYYY')}`}</b>
              </h6>
            </Col>
          </Row>
          <Row>
            <Col lg={9} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <h4 className="card-heading">Leads por empresa</h4>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-5">
                    <Col
                      xs={6}
                      sm="auto"
                      className="flex-sm-grow-1 border-start py-3"
                    >
                      <h3 className="subtitle text-gray-500">Total</h3>
                      <div className="h1 text-primary">
                        {leads.leadsByCompany
                          .reduce((total, data) => total + data.total_leads, 0)
                          .toLocaleString('pt-BR')}
                      </div>
                      <p className="mb-0">
                        <Badge
                          className="me-3"
                          bg="success-light"
                          text="success"
                          title="Abertos"
                        >
                          <FaArrowUp className="me-2" />
                          {leads.leadsOpen.toLocaleString('pt-BR')}
                        </Badge>
                        <Badge bg="danger-light" text="danger" title="Fechados">
                          <FaArrowDown className="me-2" />
                          {leads.leadsClosed.toLocaleString('pt-BR')}
                        </Badge>
                      </p>
                    </Col>
                    {leads.leadsByCompany.map((company) => (
                      <Col
                        key={v4()}
                        xs={6}
                        sm="auto"
                        className="flex-sm-grow-1 border-start py-3 d-flex align-items-center"
                      >
                        <div>
                          <h3 className="subtitle text-gray-500 fw-normal">
                            {company.Company.name}
                          </h3>
                          <div className="h4 fw-normal text-dark">
                            {company.total_leads.toLocaleString('pt-BR')}
                          </div>
                          <p className="mb-0">
                            <Badge
                              className="me-3"
                              bg="success-light"
                              text="success"
                              title="Abertos"
                            >
                              <FaArrowUp className="me-2" />
                              {company.total_open.toLocaleString('pt-BR')}
                            </Badge>
                            <Badge
                              bg="danger-light"
                              text="danger"
                              title="Fechados"
                            >
                              <FaArrowDown className="me-2" />
                              {company.total_closed.toLocaleString('pt-BR')}
                            </Badge>
                          </p>
                        </div>
                      </Col>
                    ))}
                  </Row>
                  <BarChart
                    label="Total"
                    labels={leads.leadsByCompany.map((val) => val.Company.name)}
                    values={leads.leadsByCompany.map((val) => val.total_leads)}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col xl={3}>
              <div className="card-adjust-height-xl">
                <Activity
                  title="Não atribuídos"
                  data={leads.last30LeadsWithoutUser.map((val) => {
                    return {
                      name: val.name,
                      time: moment.utc(val.created_at).format('DD/MM/YYYY'),
                      text: val.protocol,
                      link: `/leads/${val.id}/edit`,
                      img: val.LeadIvrOpenNewCanceled
                        ? '/img/phone-circle.png'
                        : false,
                    };
                  })}
                  appendHeader={
                    <>
                      <Col sm={12} className="border-bottom d-flex">
                        <div className="h6 text-gray-500 me-3">Total </div>
                        <div className="h2 text-primary">
                          {leads.leadsWithoutUser.toLocaleString('pt-BR')}
                        </div>
                      </Col>
                      <Col sm={12} className="mt-3">
                        <h6 className="subtitle text-gray-500">
                          Últimos 30 leads
                        </h6>
                      </Col>
                    </>
                  }
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={6} className="mb-4">
              <Card className="h-100">
                <Card.Header>
                  <h5 className="card-heading">Leads por status</h5>
                </Card.Header>
                <Card.Body>
                  <DonutChart
                    labels={['Abertos', 'Fechados']}
                    values={[leads.leadsOpen, leads.leadsClosed]}
                    color={['green', 'red']}
                    cutout="70%"
                    hideLegend
                    noDatasetBorder
                  />
                </Card.Body>
                <Card.Footer className="bg-white">
                  <h3 className="subtitle text-gray-500 fw-normal text-center">
                    Total
                  </h3>
                  <Row className="w-100 justify-content-center align-items-center">
                    <Col sm="auto">
                      <div className="h4 mb-0">
                        {leads.leadsOpen.toLocaleString('pt-BR')}
                      </div>
                    </Col>
                    <Col sm={6}>
                      {' '}
                      <span className="text-muted me-3">Abertos</span>
                      <Badge bg="success-light" text="success">
                        <FaArrowUp className="me-2" />
                        {(
                          (leads.leadsOpen /
                            (leads.leadsOpen + leads.leadsClosed)) *
                          100
                        ).toFixed(2)}{' '}
                        %
                      </Badge>
                    </Col>
                  </Row>
                  <Row className="w-100 justify-content-center align-items-center">
                    <Col sm="auto">
                      <div className="h4 mb-0">
                        {leads.leadsClosed.toLocaleString('pt-BR')}
                      </div>
                    </Col>
                    <Col sm={6}>
                      {' '}
                      <span className="text-muted me-2">Fechados</span>
                      <Badge bg="danger-light" text="danger">
                        <FaArrowDown className="me-2" />
                        {(
                          (leads.leadsClosed /
                            (leads.leadsOpen + leads.leadsClosed)) *
                          100
                        ).toFixed(2)}{' '}
                        %
                      </Badge>
                    </Col>
                  </Row>
                </Card.Footer>
              </Card>
            </Col>
            <Col lg={6}>
              <div className="card-adjust-height-xl">
                <Activity
                  title="Meus leads"
                  data={leads.last30LeadsByUser.map((val) => {
                    return {
                      name: val.name,
                      time: moment.utc(val.created_at).format('DD/MM/YYYY'),
                      text: val.protocol,
                      link: `/leads/${val.id}/edit`,
                      img: val.LeadIvrOpenNewCanceled
                        ? '/img/phone-circle.png'
                        : false,
                    };
                  })}
                  appendHeader={
                    <>
                      <Col
                        sm={12}
                        className="border-bottom d-flex justify-content-center align-items-center"
                      >
                        <div className="h6 text-gray-500 me-3">Total </div>
                        <div className="h2 text-primary me-4">
                          {leads.leadsByUser.total.toLocaleString('pt-BR')}
                        </div>
                        <div className="h6 text-gray-500 me-3">Abertos </div>
                        <div className="h2 text-success me-4">
                          {leads.leadsByUser.open.toLocaleString('pt-BR')}
                        </div>
                        <div className="h6 text-gray-500 me-3">Fechados </div>
                        <div className="h2 text-danger">
                          {leads.leadsByUser.closed.toLocaleString('pt-BR')}
                        </div>
                      </Col>
                      <Col sm={12} className="mt-3">
                        <h6 className="subtitle text-gray-500">
                          30 leads mais antigos abertos
                        </h6>
                      </Col>
                    </>
                  }
                />
              </div>
            </Col>
          </Row>
        </>
      )}
    </div>
  );
}
